// Выпадающий список в стиле сайта вместо системного <select> (разметка .sel/.sel-btn/.sel-menu).
// Настоящий <select> остаётся скрытым: внешний код может писать select.value = … (с событием change или без) —
// значение уходит в onChange.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { keepInList } from '../lib/scroll';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  id?: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  /** компактный вид (валюта) */
  compact?: boolean;
  /** без рамки (сортировки в панелях) */
  plain?: boolean;
  ariaLabel?: string;
  /** классы обёртки (например, month-only) */
  className?: string;
  hidden?: boolean;
}

const valueDesc = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')!;

// Открыт может быть только один список
let closeOpen: (() => void) | null = null;

export function Select({ id, value, options, onChange, compact, plain, ariaLabel, className, hidden }: SelectProps) {
  const wrap = useRef<HTMLDivElement>(null);
  const sel = useRef<HTMLSelectElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLUListElement>(null);
  const latest = useRef({ value, onChange });
  latest.current = { value, onChange };
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const [active, setActive] = useState(-1);

  // Перехват .value: запись извне сразу попадает в состояние владельца
  useLayoutEffect(() => {
    const el = sel.current;
    if (!el) return;
    Object.defineProperty(el, 'value', {
      configurable: true,
      get() { return valueDesc.get!.call(this); },
      set(v: string) {
        valueDesc.set!.call(this, v);
        const now = valueDesc.get!.call(this) as string;
        if (now !== latest.current.value) latest.current.onChange(now);
      },
    });
    const onNativeChange = () => {
      const now = valueDesc.get!.call(el) as string;
      if (now !== latest.current.value) latest.current.onChange(now);
    };
    el.addEventListener('change', onNativeChange);
    return () => {
      el.removeEventListener('change', onNativeChange);
      delete (el as { value?: string }).value;
    };
  }, []);

  // Значение владельца → скрытый <select>
  useLayoutEffect(() => {
    const el = sel.current;
    if (el && valueDesc.get!.call(el) !== value) valueDesc.set!.call(el, value);
  }, [value, options]);

  const close = () => {
    setOpen(false);
    setUp(false);
    if (closeOpen === close) closeOpen = null;
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && e.target instanceof Node && !wrap.current.contains(e.target)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    keepInList(menu.current, menu.current?.querySelector('.on'));
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const current = options[selectedIndex];

  const openList = () => {
    closeOpen?.();
    closeOpen = close;
    setActive(selectedIndex);
    // Если снизу мало места — раскрываем вверх
    const r = btn.current?.getBoundingClientRect();
    const h = Math.min(320, options.length * 40 + 20);
    setUp(Boolean(r && window.innerHeight - r.bottom < h && r.top > 320));
    setOpen(true);
  };

  const choose = (v: string) => {
    close();
    btn.current?.focus();
    if (v !== latest.current.value) {
      const el = sel.current;
      if (el) valueDesc.set!.call(el, v);
      latest.current.onChange(v);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key) && !open) { e.preventDefault(); openList(); return; }
    if (!open || !options.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = (active + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      setActive(next);
      keepInList(menu.current, menu.current?.children[next]);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const o = options[active];
      if (o) choose(o.value);
    }
  };

  const cls = ['sel', compact ? 'sel-compact' : '', plain ? 'sel-plain' : '', className || '', open ? 'open' : '', up ? 'up' : ''].filter(Boolean).join(' ');
  return (
    <div ref={wrap} className={cls} hidden={hidden}>
      <select id={id} ref={sel} className={plain ? 'plain sel-native' : 'sel-native'} tabIndex={-1} aria-label={ariaLabel} defaultValue={value} data-enhanced="1">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <button ref={btn} type="button" className="sel-btn" aria-haspopup="listbox" aria-label={ariaLabel} onClick={() => (open ? close() : openList())} onKeyDown={onKeyDown}>
        <span>{current ? current.label : ''}</span><Icon name="down" />
      </button>
      <ul ref={menu} className="sel-menu" role="listbox" hidden={!open}>
        {open && options.map((o, i) => (
          <li
            key={o.value}
            role="option"
            data-value={o.value}
            className={[i === selectedIndex ? 'on' : '', i === active ? 'active' : ''].filter(Boolean).join(' ') || undefined}
            aria-selected={i === selectedIndex}
            onMouseDown={(e) => { e.preventDefault(); choose(o.value); }}
          >
            <span>{o.label}</span>{i === selectedIndex && <Icon name="check" />}
          </li>
        ))}
      </ul>
    </div>
  );
}
